import * as SelectPrimitive from '@radix-ui/react-select'

import { ChevronDown } from '../icons'
import { twMerge } from 'tailwind-merge'

export type SelectGenericProps = {
  value: string
  label: string
}

export function SelectGeneric({
  items,
  name,
  onChange,
  className,
  selected,
  disabled = false,
  placeholder = 'Seleccionar',
  error = false,
  RenderPlaceholder,
  RenderItem,
}: {
  selected?: SelectGenericProps
  className?: string
  name: string
  onChange: (item: SelectGenericProps) => void
  items: SelectGenericProps[]
  disabled?: boolean
  placeholder?: string
  error?: boolean
  RenderPlaceholder: () => React.ReactElement
  RenderItem: ({ value, label }: SelectGenericProps) => React.ReactElement
}) {
  return (
    <SelectPrimitive.Root
      name={name}
      onValueChange={(value) => {
        if (!disabled) {
          const foundItem = items.find((item) => item.value === value)
          if (foundItem) {
            onChange(foundItem)
          }
        }
      }}
      value={selected?.value}
      disabled={disabled}>
      <SelectPrimitive.Trigger
        aria-label={name}
        className={twMerge(
          'flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 py-2 text-sm leading-none transition-colors focus:border-admin-blue focus:outline-none focus:ring-1 focus:ring-admin-blue',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 hover:border-gray-400',
          disabled && 'cursor-not-allowed bg-gray-50 opacity-50',
          className
        )}>
        <SelectPrimitive.Value>
          <RenderPlaceholder />
        </SelectPrimitive.Value>

        <SelectPrimitive.Icon className="transition-transform duration-200 data-[state=open]:rotate-180">
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          className="z-50 max-h-[320px] w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
          position="popper"
          sideOffset={4}>
          <SelectPrimitive.Viewport className="max-h-[250px] overflow-y-auto p-1">
            {items.map((item) => (
              <SelectPrimitive.Item
                value={item.value}
                key={`select_item_${item.value}`}
                className={twMerge(
                  'cursor-pointer select-none rounded-sm px-3 py-2 text-sm outline-none transition-colors',
                  selected?.value === item.value
                    ? 'bg-blue-100 font-medium text-blue-800'
                    : 'hover:bg-gray-50'
                )}>
                <SelectPrimitive.ItemText>
                  <RenderItem value={item.value} label={item.label} />
                </SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

export function SimpleSelectGeneric({
  items,
  name,
  onChange,
  selected,
  disabled = false,
  placeholder = 'Seleccionar',
  className,
  error = false,
}: {
  items: SelectGenericProps[]
  name: string
  onChange: (item: SelectGenericProps) => void
  selected?: SelectGenericProps
  disabled?: boolean
  placeholder?: string
  className?: string
  error?: boolean
}) {
  return (
    <SelectGeneric
      items={items}
      name={name}
      onChange={onChange}
      selected={selected}
      disabled={disabled}
      error={error}
      className={className}
      RenderPlaceholder={() => (
        <div
          className={twMerge(
            'text-sm',
            selected ? 'text-gray-900' : 'text-gray-500'
          )}>
          {selected ? selected.label : placeholder}
        </div>
      )}
      RenderItem={({ label }) => (
        <div className="flex items-center text-sm">{label}</div>
      )}
    />
  )
}
